/* ==========================================================================
   Tire Tread Depth Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-ttc]');
  if (!root) return;

  var MM_PER_32ND = 0.79375;
  var LEGAL_32ND = 2;      /* legal minimum in most of the US */
  var WET_32ND   = 4;      /* wet performance falls off sharply below this */
  var SNOW_32ND  = 6;      /* effective snow traction needs this much */

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    var depth = input.unit === 'mm' ? input.depth / MM_PER_32ND : input.depth;
    if (!isFinite(depth) || depth < 0) {
      return { ok: false, errors: ['Enter your current tread depth.'] };
    }
    var newDepth = isFinite(input.newDepth) && input.newDepth > LEGAL_32ND ? input.newDepth : 10;
    if (depth > newDepth) {
      return { ok: false, errors: ['Current depth is greater than the new depth. Check both figures.'] };
    }
    if (!isFinite(input.miles) || input.miles <= 0) {
      return { ok: false, errors: ['Enter how many miles you drive per year.'] };
    }

    var usable = depth - LEGAL_32ND;
    var used = newDepth - depth;

    /* Wear rate from this tire's own history where we have it, otherwise a
       typical 1/32nd every 4,000 miles. */
    var perThirtySecond;
    var basis;
    if (isFinite(input.already) && input.already > 1000 && used > 0.5) {
      perThirtySecond = input.already / used;
      basis = 'your own wear rate';
    } else {
      perThirtySecond = 4000;
      basis = 'a typical wear rate';
    }

    var milesLeft = Math.max(0, usable * perThirtySecond);
    var monthsLeft = (milesLeft / input.miles) * 12;

    var status;
    if (depth <= LEGAL_32ND) {
      status = { key: 'critical', label: num(depth, 1) + '/32 worn out',
                 note: 'At or below the 2/32 legal minimum. These are done.' };
    } else if (depth <= 3) {
      status = { key: 'now', label: num(depth, 1) + '/32 replace now',
                 note: 'Almost no wet grip left and close to illegal. Replace before the next rain.' };
    } else if (depth < WET_32ND) {
      status = { key: 'soon', label: num(depth, 1) + '/32 replace soon',
                 note: 'Below 4/32 wet braking and hydroplaning resistance fall off steeply.' };
    } else if (depth < SNOW_32ND) {
      status = { key: 'monitor', label: num(depth, 1) + '/32 monitor',
                 note: 'Fine in the dry and wet. Not enough for meaningful snow traction.' };
    } else {
      status = { key: 'good', label: num(depth, 1) + '/32 good',
                 note: 'Plenty of tread for all conditions.' };
    }

    var warnings = [];
    if (depth >= LEGAL_32ND && depth < WET_32ND) {
      warnings.push('Legal is not the same as safe. Wet stopping distance at 3/32 can be double what it was at 10/32.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Miles remaining', value: milesLeft > 0 ? comma(Math.round(milesLeft / 100) * 100) + ' mi' : '0 mi',
          sub: milesLeft > 0
            ? 'About ' + num(monthsLeft, 0) + ' months at ' + comma(input.miles) + ' miles a year, using ' + basis + '.'
            : 'At or past the legal limit.' },
        { label: 'Tread remaining', value: num(depth, 1) + '/32 in',
          sub: num(depth * MM_PER_32ND, 1) + ' mm. ' + num(usable, 1) + '/32 above the legal minimum.' }
      ],
      rows: [
        { head: 'Measurements' },
        { label: 'Current', value: num(depth, 1) + '/32  (' + num(depth * MM_PER_32ND, 2) + ' mm)' },
        { label: 'When new', value: num(newDepth, 1) + '/32' },
        { label: 'Worn away', value: num(used, 1) + '/32' },
        { label: 'Life used', value: num((used / (newDepth - LEGAL_32ND)) * 100, 0) + '%' },
        { head: 'Wear rate' },
        { label: 'Basis', value: basis },
        { label: 'Miles per 1/32', value: comma(Math.round(perThirtySecond)) },
        { label: 'Miles remaining', value: comma(Math.round(milesLeft)) },
        { head: 'Thresholds' },
        { label: 'Legal minimum', value: '2/32 in' },
        { label: 'Wet performance', value: 'below 4/32 falls off' },
        { label: 'Snow traction', value: 'needs 6/32' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "depth",
          "key": "d",
          "type": "number"
      },
      {
          "name": "unit",
          "key": "u",
          "type": "select"
      },
      {
          "name": "newDepth",
          "key": "nd",
          "type": "number"
      },
      {
          "name": "miles",
          "key": "m",
          "type": "number"
      },
      {
          "name": "already",
          "key": "a",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-ttc__figure">'
        + '<div class="pc-ttc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-ttc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-ttc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-ttc__row pc-ttc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-ttc__row">'
        + '<span class="pc-ttc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-ttc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_TTC = { compute: compute };
})();
